import { PRICE_DECIMALS } from './money';

interface UseCurrencyFieldOptions {
  /** Valor em reais. `null` deixa o campo vazio. */
  value: number | null;
  /** Recebe `null` quando o usuário apaga tudo. */
  onValueChange: (value: number | null) => void;
}

interface CurrencyFieldProps {
  type: 'text';
  inputMode: 'numeric';
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CENTS_PER_UNIT = 10 ** PRICE_DECIMALS;

/** `3990` (centavos) vira `39,90`; `123456` vira `1.234,56`. */
export function formatCents(cents: number): string {
  return (cents / CENTS_PER_UNIT).toLocaleString('pt-BR', {
    minimumFractionDigits: PRICE_DECIMALS,
    maximumFractionDigits: PRICE_DECIMALS,
  });
}

/** Reais para centavos, sem passar por multiplicação que erra (`0.29 * 100`). */
export function toCents(value: number): number {
  return Number(value.toFixed(PRICE_DECIMALS).replace('.', ''));
}

/**
 * Campo de moeda com máscara: os dígitos preenchem os centavos da direita
 * para a esquerda, e o campo tem **sempre** duas casas.
 *
 * Diferente de `useMoneyField`, aqui não existe separador para digitar — e é
 * isso que resolve o problema de origem. Enquanto o campo aceitava texto
 * livre, `39.90` e `39,90` dependiam de alguém acertar qual separador era
 * decimal; digitando `3990` o operador vê `39,90` aparecer caractere a
 * caractere, e o que ele lê é exatamente o que será cobrado.
 *
 * `useMoneyField` continua servindo o painel, onde o campo de preço de produto
 * aceita colar valor de outra tela. Aqui, num campo que define o preço de
 * todos os assinantes, a máscara vale o atrito.
 */
export function useCurrencyField({
  value,
  onValueChange,
}: UseCurrencyFieldOptions): CurrencyFieldProps {
  // `null` é campo vazio, e ponto — sem estado interno de "já digitou".
  // Havia um, e ele mentia quando quem manda no valor era a tela e não o
  // teclado: depois de salvar, ou ao trocar o tipo do desconto, o valor
  // voltava a `null` mas o campo continuava exibindo "0,00", como se alguém
  // tivesse digitado um zero. Zero digitado continua sendo `0`, não `null`,
  // então a distinção não precisa de memória.
  const text = value === null ? '' : formatCents(toCents(value));

  return {
    type: 'text',
    inputMode: 'numeric',
    value: text,
    onChange: (event) => {
      const digits = event.target.value.replace(/\D/g, '');

      if (!digits) {
        onValueChange(null);
        return;
      }

      // Limite de sanidade: `numeric(10,2)` no banco comporta 8 dígitos
      // inteiros; sem o corte, segurar uma tecla produz um número que o
      // servidor recusaria só depois de enviar.
      const capped = digits.slice(0, 10);
      onValueChange(Number(capped) / CENTS_PER_UNIT);
    },
  };
}
